/**
 * EDC Glossary finder (templates/page.glossary.liquid, merchant 2026-10-30 rebuild).
 * Patterns: MDN Glossary + Algolia DocSearch (instant in-place filtering, match highlighting, Enter opens the best match, "/" focuses,
 * Esc clears, arrow keys move through results), Apple Developer docs (sticky category nav with live counts and a clear active state),
 * iOS Contacts / Investopedia (A–Z groups, sticky letter headers, letter rail with scroll-spy).
 * The toolbar sticks under the site header; every jump lands exactly below it (scroll-margin from measured heights).
 * State lives in the URL (?q=…&topic=…) so a filtered view can be shared.
 */
const root = document.querySelector('[data-gf]');
if (root) {
  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => [...r.querySelectorAll(s)];
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const bar = $('[data-gf-bar]');
  const input = $('[data-gf-q]');
  const clearBtn = $('[data-gf-clear]');
  const status = $('[data-gf-status]');
  const hint = $('[data-gf-hint]');
  const empty = $('[data-gf-empty]');
  const list = $('[data-gf-list]');
  const results = $('#gf-results');
  const topicBtns = $$('[data-gf-topic]');
  const letters = $$('[data-gf-letter]');
  const groups = $$('[data-gf-group]');
  const items = $$('[data-gf-item]').map((el) => {
    const name = $('[data-gf-name]', el);
    const desc = $('[data-gf-desc]', el);
    return {
      el,
      link: $('a', el),
      group: el.closest('[data-gf-group]'),
      topics: ` ${el.dataset.topics || ''} `,
      text: el.dataset.text || '',
      desc: el.dataset.desc || '',
      nameEl: name,
      descEl: desc,
      name: name?.textContent || '',
      descText: desc?.textContent || '',
    };
  });

  let topic = 'all';
  let best = null;

  /* ---- Sticky offsets: site header height + toolbar height ---- */
  const header = document.querySelector('.sticky.top-0.z-40') || document.querySelector('header');
  const measure = () => {
    const top = header && getComputedStyle(header).position === 'sticky' ? Math.round(header.getBoundingClientRect().height) : 0;
    root.style.setProperty('--gf-top', `${top}px`);
    root.style.setProperty('--gf-bar-h', `${Math.round(bar.getBoundingClientRect().height)}px`);
  };
  measure();
  window.addEventListener('resize', measure);
  new ResizeObserver(measure).observe(bar);

  const jump = (el) => el?.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
  const toListTop = () => {
    // Only scroll when the list top is out of view under the toolbar.
    const offset = parseFloat(getComputedStyle(root).getPropertyValue('--gf-top')) + bar.offsetHeight;
    const top = results.getBoundingClientRect().top;
    if (top < offset || top > window.innerHeight * 0.6) jump(results);
  };

  /* ---- Matching + highlighting ---- */
  const mark = (text, words) => {
    let out = esc(text);
    for (const w of words) out = out.replace(new RegExp(`(${reEsc(esc(w))})`, 'ig'), '<mark>$1</mark>');
    return out;
  };
  const scoreOf = (it, words, q) => {
    const n = it.name.toLowerCase();
    if (!words.every((w) => it.text.includes(w) || it.desc.includes(w))) return -1;
    if (n === q || it.text.split(' ')[0] === q) return 4;
    if (n.startsWith(q)) return 3;
    if (words.every((w) => it.text.includes(w))) return 2;
    return 1;
  };

  const update = ({ scroll = false } = {}) => {
    const q = input.value.trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    const counts = { all: 0 };
    let shown = 0;
    best = null;
    let bestScore = 0;

    for (const it of items) {
      const s = words.length ? scoreOf(it, words, q) : 0;
      const matchQ = !words.length || s > 0;
      if (matchQ) {
        counts.all += 1;
        for (const t of it.topics.trim().split(/\s+/)) if (t) counts[t] = (counts[t] || 0) + 1;
      }
      const visible = matchQ && (topic === 'all' || it.topics.includes(` ${topic} `));
      it.el.hidden = !visible;
      if (!visible) continue;
      shown += 1;
      if (s > bestScore) { bestScore = s; best = it; }
      if (it.nameEl) it.nameEl.innerHTML = words.length ? mark(it.name, words) : esc(it.name);
      if (it.descEl) it.descEl.innerHTML = words.length ? mark(it.descText, words) : esc(it.descText);
    }
    if (!best && words.length) best = items.find((it) => !it.el.hidden) || null;

    // Groups and letters.
    const live = new Set();
    for (const g of groups) {
      const any = !!g.querySelector('[data-gf-item]:not([hidden])');
      g.hidden = !any;
      if (any) live.add(g.dataset.gfGroup);
    }
    for (const a of letters) {
      const on = live.has(a.dataset.gfLetter);
      a.toggleAttribute('data-off', !on);
      a.setAttribute('aria-disabled', String(!on));
      a.tabIndex = on ? 0 : -1;
    }

    // Category counts follow the search (faceted search pattern).
    for (const b of topicBtns) {
      const n = counts[b.dataset.gfTopic] || 0;
      const el = $(`[data-gf-n="${CSS.escape(b.dataset.gfTopic)}"]`);
      if (el) el.textContent = n;
      b.toggleAttribute('data-empty', n === 0 && b.dataset.gfTopic !== 'all');
      b.setAttribute('aria-pressed', String(b.dataset.gfTopic === topic));
    }

    // Status line.
    const topicName = topic === 'all' ? '' : topicBtns.find((b) => b.dataset.gfTopic === topic)?.dataset.gfTopicName || '';
    let line = words.length
      ? root.dataset.lResults.replace('__COUNT__', shown).replace('__Q__', input.value.trim())
      : root.dataset.lCount.replace('__COUNT__', shown);
    if (topicName) line += root.dataset.lIn.replace('__T__', topicName);
    status.textContent = line;
    hint.hidden = !(words.length && best);
    if (best) hint.textContent = root.dataset.lEnter.replace('__T__', best.name);
    for (const r of $$('[data-gf-reset]')) r.hidden = !(words.length || topic !== 'all');
    clearBtn.hidden = !input.value;
    empty.hidden = shown > 0;
    list.hidden = shown === 0;

    // URL state.
    const url = new URL(location.href);
    words.length ? url.searchParams.set('q', input.value.trim()) : url.searchParams.delete('q');
    topic !== 'all' ? url.searchParams.set('topic', topic) : url.searchParams.delete('topic');
    history.replaceState(history.state, '', url);

    if (scroll) toListTop();
    spy();
  };

  /* ---- Events ---- */
  let typingTimer;
  input.addEventListener('input', () => {
    update();
    clearTimeout(typingTimer);
    typingTimer = setTimeout(toListTop, 250);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (best?.link) location.href = best.link.href;
    } else if (e.key === 'Escape') {
      if (input.value) { input.value = ''; update(); } else input.blur();
    } else if (e.key === 'ArrowDown') {
      const first = items.find((it) => !it.el.hidden);
      if (first) { e.preventDefault(); first.link.focus(); }
    }
  });
  clearBtn.addEventListener('click', () => { input.value = ''; update(); input.focus(); });

  // Arrow keys inside results.
  list.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const vis = items.filter((it) => !it.el.hidden);
    const i = vis.findIndex((it) => it.link === document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    if (e.key === 'ArrowUp' && i === 0) { input.focus(); return; }
    vis[Math.max(0, Math.min(vis.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))]?.link.focus();
  });

  for (const b of topicBtns) {
    b.addEventListener('click', () => {
      topic = b.dataset.gfTopic;
      update();
      jump(results);
    });
  }
  for (const r of $$('[data-gf-reset]')) {
    r.addEventListener('click', () => { input.value = ''; topic = 'all'; update(); jump(results); });
  }
  for (const a of letters) {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      if (a.hasAttribute('data-off')) return;
      const g = document.getElementById(`letter-${a.dataset.gfLetter}`);
      jump(g);
    });
  }

  // "/" focuses the search from anywhere (DocSearch / GitHub convention).
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    const el = document.activeElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
    if (document.querySelector('dialog[open]')) return;
    e.preventDefault();
    input.focus({ preventScroll: true });
  });

  /* ---- Scroll-spy: highlight the letter currently under the toolbar ---- */
  let spyQueued = false;
  function spy() {
    if (spyQueued) return;
    spyQueued = true;
    requestAnimationFrame(() => {
      spyQueued = false;
      // Sticky bar (desktop): its real bottom edge. Phones (bar scrolls away, A–Z lives in the dock sheet): the header's bottom.
      const sticky = getComputedStyle(bar).position === 'sticky';
      const line = (sticky ? bar.getBoundingClientRect().bottom : Math.max(0, header?.getBoundingClientRect().bottom || 0)) + 24;
      let current = null;
      for (const g of groups) {
        if (g.hidden) continue;
        if (g.getBoundingClientRect().top <= line) current = g.dataset.gfGroup;
        else break;
      }
      const listRect = list.getBoundingClientRect();
      if (listRect.bottom < line) current = null;
      for (const a of letters) a.toggleAttribute('data-current', a.dataset.gfLetter === current);
      // Phones: the letter strip scrolls sideways; keep the current letter in view (horizontal only, never moves the page).
      const strip = letters[0]?.closest('ol');
      const on = current && letters.find((a) => a.dataset.gfLetter === current);
      if (strip && on && strip.scrollWidth > strip.clientWidth && current !== spy.last) {
        const li = on.parentElement;
        const want = li.offsetLeft - (strip.clientWidth - li.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, want), behavior: reduce() ? 'auto' : 'smooth' });
      }
      spy.last = current;
    });
  }
  window.addEventListener('scroll', spy, { passive: true });


  /* ---- Preview panel (desktop ≥1280px): hover / focus / arrow keys show the term; default = Term of the day ---- */
  const peek = $('[data-gf-peek]');
  if (peek) {
    const P = (k) => $(`[data-p="${k}"]`, peek);
    const topicName = (h) => topicBtns.find((b) => b.dataset.gfTopic === h)?.dataset.gfTopicName || '';
    const labelDefault = P('label').textContent;
    const labelPreview = root.dataset.lPreview || 'Preview';
    let shownItem = null;
    const show = (it, isDefault = false) => {
      if (!it || it === shownItem) return;
      shownItem = it;
      const d = it.el.dataset;
      const term = d.term || it.name;
      P('label').textContent = isDefault ? labelDefault : labelPreview;
      P('kind').textContent = d.kind || '';
      P('topics').textContent = d.topics ? d.topics.split(' ').map(topicName).filter(Boolean).join(' · ') : '';
      const tile = P('tile');
      tile.textContent = term;
      tile.classList.remove('gh-tile--lg', 'gh-tile--md', 'gh-tile--sm');
      tile.classList.add(term.length > 8 ? 'gh-tile--sm' : term.length > 4 ? 'gh-tile--md' : 'gh-tile--lg');
      P('name').textContent = it.name;
      P('desc').textContent = it.descText;
      P('ex').textContent = d.ex || '';
      P('ex').hidden = !d.ex;
      const n = Number(d.src || 0);
      P('src').hidden = n === 0;
      P('src').textContent = P('src').dataset.label.replace('__N__', n);
      P('url').href = it.link.href;
      peek.classList.remove('is-swap');
      void peek.offsetWidth;
      peek.classList.add('is-swap');
    };
    const wide = window.matchMedia('(min-width: 80rem)');
    let hoverTimer;
    for (const it of items) {
      it.link.addEventListener('mouseenter', () => { if (!wide.matches) return; clearTimeout(hoverTimer); hoverTimer = setTimeout(() => show(it), 90); });
      it.link.addEventListener('focus', () => wide.matches && show(it));
    }
    list.addEventListener('mouseleave', () => clearTimeout(hoverTimer));
    // Default: Term of the day (same for everyone on a given day), then the best match while searching.
    const day = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 864e5);
    const pool = items.filter((it) => it.el.dataset.kind !== 'Brand' && !it.topics.includes(' brands '));
    const tod = pool.length ? pool[day % pool.length] : items[0];
    show(tod, true);
    input.addEventListener('input', () => { if (wide.matches) show(best || tod, !best); });
  }

  /* ---- Initial state from the URL (?q, ?topic) or a #term-x deep link ---- */
  const params = new URLSearchParams(location.search);
  const initialTopic = params.get('topic');
  if (initialTopic && topicBtns.some((b) => b.dataset.gfTopic === initialTopic)) topic = initialTopic;
  if (params.get('q')) input.value = params.get('q');
  update();
  if (initialTopic || params.get('q')) requestAnimationFrame(() => jump(results));
  const hash = decodeURIComponent(location.hash.slice(1));
  if (hash.startsWith('term-')) {
    const row = document.getElementById(hash);
    if (row) requestAnimationFrame(() => { jump(row); row.setAttribute('data-flash', ''); setTimeout(() => row.removeAttribute('data-flash'), 2400); });
  }
}
